import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { autoMapColumns, MYLES_CONTACT_FIELDS, MYLES_COMPANY_FIELDS, type SourcePreset } from "./sourceMapping";

interface Props {
    csvHeaders: string[];
    sourceKey: SourcePreset["key"];
    mapping: Record<string, { mylesField: string; table: "contact" | "company" }>;
    onMappingChange: (mapping: Record<string, { mylesField: string; table: "contact" | "company" }>) => void;
    onNext: () => void;
    onBack: () => void;
}

const allFields = [
    ...MYLES_CONTACT_FIELDS.map((f) => ({ value: `contact.${f}`, label: f, table: "contact" as const })),
    ...MYLES_COMPANY_FIELDS.map((f) => ({ value: `company.${f}`, label: f, table: "company" as const })),
];

export function ImportMappingStep({ csvHeaders, sourceKey, mapping, onMappingChange, onNext, onBack }: Props) {
    // Auto-map on mount
    useEffect(() => {
        if (Object.keys(mapping).length === 0) {
            const autoMapped = autoMapColumns(csvHeaders, sourceKey);
            onMappingChange(autoMapped);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFieldChange = (csvHeader: string, fieldKey: string) => {
        if (fieldKey === "skip") {
            const next = { ...mapping };
            delete next[csvHeader];
            onMappingChange(next);
        } else {
            const [table, field] = fieldKey.split(".") as ["contact" | "company", string];
            onMappingChange({ ...mapping, [csvHeader]: { mylesField: field, table } });
        }
    };

    const mappedCount = Object.keys(mapping).length;

    return (
        <div>
            <h2 className="mb-2 text-lg font-semibold">Map Columns</h2>
            <p className="mb-4 text-sm text-muted-foreground">{mappedCount} of {csvHeaders.length} columns mapped</p>
            <div className="space-y-3">
                {csvHeaders.map((header) => {
                    const current = mapping[header];
                    const currentValue = current ? `${current.table}.${current.mylesField}` : "skip";
                    return (
                        <div key={header} className="flex items-center gap-4">
                            <span className="w-48 truncate text-sm font-medium">{header}</span>
                            <span className="text-muted-foreground">→</span>
                            <Select value={currentValue} onValueChange={(v) => handleFieldChange(header, v)}>
                                <SelectTrigger className="w-64">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="skip">Skip</SelectItem>
                                    {allFields.map((f) => (
                                        <SelectItem key={f.value} value={f.value}>
                                            {f.table}: {f.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    );
                })}
            </div>
            <div className="mt-6 flex gap-2">
                <Button variant="outline" onClick={onBack}>Back</Button>
                <Button onClick={onNext} disabled={mappedCount === 0}>Preview Import</Button>
            </div>
        </div>
    );
}
